<template>
  <!-- Modern look (soft): label above the field, manual input via a link, never rounded -->
  <div v-if="soft">
    <div class="create-address__field">
      <span v-if="label" class="acq-label">{{ label }}<span v-if="required" class="mdlg-req"> *</span></span>
      <address-search-field v-model="selectedAddressSearchItem" :allow-manual-input="allowManualInput" :clearable="clearable" :color="color"
                            :dense="dense" :disabled="useManualInput" :item-color="color" :items="addressSearchItems"
                            :loading="addressSearchLoading" :outlined="outlined" :rules="rules" :search-active="hasValidSearchInput"
                            :search-input.sync="addressSearchInput" hide-details="auto" placeholder="Adresse suchen, z. B. Musterstraße 1, Berlin"
                            prepend-inner-icon="mdi-map-marker-outline" @input="handleAddressSearchInput" @manual="useManualInput = true"
                            @update:search-input="handleAddressSearch"/>
    </div>
    <button v-if="allowManualInput" class="add-link mt-2" type="button" @click="useManualInput = !useManualInput">
      <v-icon x-small>{{ useManualInput ? 'mdi-magnify' : 'mdi-pencil-outline' }}</v-icon>
      {{ useManualInput ? 'Zur Adresssuche wechseln (manuelle Eingabe verwerfen)' : 'Adresse manuell eingeben' }}
    </button>
    <v-slide-y-transition>
      <div v-if="useManualInput" class="create-address__manual">
        <div class="create-address__grid create-address__grid--wide-narrow">
          <div class="create-address__field">
            <span class="acq-label">Straße<span class="mdlg-req"> *</span></span>
            <v-text-field v-model="street" :color="color" :rules="[RuleFactory.required()]" dense hide-details="auto" outlined
                          @input="handleManualInput"/>
          </div>
          <div class="create-address__field">
            <span class="acq-label">Hausnummer</span>
            <v-text-field v-model="houseNumber" :color="color" dense hide-details="auto" outlined @input="handleManualInput"/>
          </div>
        </div>
        <div class="create-address__grid create-address__grid--narrow-wide">
          <div class="create-address__field">
            <span class="acq-label">PLZ<span class="mdlg-req"> *</span></span>
            <v-text-field v-model="postal" :color="color" :rules="[RuleFactory.required()]" dense hide-details="auto" inputmode="numeric"
                          outlined @input="handleManualInput"/>
          </div>
          <div class="create-address__field">
            <span class="acq-label">Ort<span class="mdlg-req"> *</span></span>
            <v-text-field v-model="city" :color="color" :rules="[RuleFactory.required()]" dense hide-details="auto" outlined
                          @input="handleManualInput"/>
          </div>
        </div>
        <div class="create-address__grid">
          <div class="create-address__field">
            <span class="acq-label">Bundesland<span class="mdlg-req"> *</span></span>
            <v-autocomplete v-model="state" :color="color" :item-color="color" :items="StateCodesVue" :rules="[RuleFactory.required()]" dense
                            hide-details="auto" outlined @input="handleManualInput"/>
          </div>
          <div class="create-address__field">
            <span class="acq-label">Land<span class="mdlg-req"> *</span></span>
            <v-autocomplete v-model="country" :color="color" :item-color="color" :items="CountryCodesVue" :rules="[RuleFactory.required()]"
                            dense hide-details="auto" outlined @input="handleManualInput"/>
          </div>
        </div>
      </div>
    </v-slide-y-transition>
  </div>
  <div v-else>
    <v-row dense>
      <v-col>
        <address-search-field v-model="selectedAddressSearchItem" :allow-manual-input="allowManualInput" :clearable="clearable"
                              :color="color" :dense="dense" :disabled="useManualInput" :item-color="color" :items="addressSearchItems"
                              :label="label" :loading="addressSearchLoading" :outlined="outlined" :prepend-icon="prependIcon"
                              :rounded="rounded" :rules="rules" :search-active="hasValidSearchInput" :search-input.sync="addressSearchInput"
                              @input="handleAddressSearchInput" @manual="useManualInput = true" @update:search-input="handleAddressSearch"/>
      </v-col>
      <v-col cols="1" v-if="allowManualInput">
        <v-tooltip bottom>
          <template v-slot:activator="{on}">
            <v-scale-transition group leave-absolute origin="center">
              <v-btn icon v-if="useManualInput" @click="useManualInput = false" v-on="on" key="disableManual">
                <v-icon>mdi-pencil-off</v-icon>
              </v-btn>
              <v-btn icon v-else @click="useManualInput = true" v-on="on" key="enableManual" :color="color">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-scale-transition>
          </template>
          <span v-if="useManualInput">Zur automatischen Eingabe wechseln</span><span v-else>Zur manuellen Eingabe wechseln</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-slide-y-transition>
      <v-row v-if="useManualInput" dense>
        <v-col cols="12" md="6">
          <v-text-field :rules="[RuleFactory.required()]" @input="handleManualInput" :color="color" :rounded="rounded"
                        label="Straße" :outlined="outlined" :dense="dense" v-model="street"/>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field :color="color" :rounded="rounded" @input="handleManualInput" label="Hausnummer"
                        :outlined="outlined"
                        :dense="dense" v-model="houseNumber"/>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field :rules="[RuleFactory.required()]" @input="handleManualInput" :color="color" :rounded="rounded"
                        label="Postleitzahl" :outlined="outlined" :dense="dense" v-model="postal"/>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field :rules="[RuleFactory.required()]" @input="handleManualInput" :color="color" :rounded="rounded"
                        label="Ort" :outlined="outlined" :dense="dense" v-model="city"/>
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete :rules="[RuleFactory.required()]" @input="handleManualInput" :color="color" :rounded="rounded"
                          label="Bundesland" :items="StateCodesVue" :outlined="outlined" :dense="dense"
                          v-model="state"/>
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete :rules="[RuleFactory.required()]" @input="handleManualInput" :color="color" :rounded="rounded"
                          label="Land" :items="CountryCodesVue" :outlined="outlined" :dense="dense" v-model="country"/>
        </v-col>
      </v-row>
    </v-slide-y-transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {StateCodes, StateCodesVue} from '@/enums/StateCodes.enum';
import {CountryCodes, CountryCodesVue} from '@/enums/CountryCodes.enum';
import {RuleFactory} from '@/helpers/ruleFactory.helper';
import {sleep} from '@/helpers/sleep.helper';
import {GeocoderAPI} from '@/classes/api/geocoder.api.class';
import {FindAddressResponseDto} from '@/classes/dto/geocoder/response/FindAddress.response.dto';
import {PlaceTemplateDto} from '@/classes/dto/_common/templates/Place.template.dto';
import {Place} from '@/interfaces/place.interface';
import {PartialPlaceTemplateDto} from '@/classes/dto/_common/templates/PartialPlace.template.dto';
import AddressSearchField from '@/components/common/address/addressSearchField.vue';

type AddressSearchItem = FindAddressResponseDto['items'][0];
export default Vue.extend({
  components: {AddressSearchField},
  props: {
    value: {
      type: Object as () => PlaceTemplateDto,
      required: true,
    },
    defaultObject: {
      type: Object as () => Place | PartialPlaceTemplateDto,
      required: false,
    },
    label: {
      type: String,
      default: 'Adresse',
    },
    color: {
      type: String,
      default: 'info',
    },
    rounded: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      required: false,
    },
    allowManualInput: {
      type: Boolean,
      default: true,
    },
    throttled: {
      type: Boolean,
      default: true,
    },
    throttleDelay: {
      type: Number,
      default: 1000,
    },
    rules: {
      type: Array as () => Array<(v: any) => boolean | string>,
      default: () => ([]),
    },
    requireStreet: {
      type: Boolean,
      default: true,
    },
    useGoogle: {
      type: Boolean,
      default: true,
    },
    useOsm: {
      type: Boolean,
      default: true,
    },
    /** Modern look: label above the field (acq-label), manual input via a link, not rounded */
    soft: {
      type: Boolean,
      default: false,
    },
    /** Only for the soft look: shows the required marker next to the label */
    required: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    addressSearchInput: '',
    selectedAddressSearchItem: null as AddressSearchItem | null,
    addressSearchItems: [] as AddressSearchItem[],
    addressSearchLoading: false,
    throttleCounter: 0,
    useManualInput: false,

    street: '',
    houseNumber: '',
    postal: '',
    city: '',
    state: null as StateCodes | null,
    country: null as CountryCodes | null,
    lat: undefined as number | undefined,
    lng: undefined as number | undefined,
  }),
  computed: {
    RuleFactory: () => RuleFactory,
    StateCodesVue: () => StateCodesVue,
    CountryCodesVue: () => CountryCodesVue,
    hasValidSearchInput(): boolean {
      return !!this.addressSearchInput && (!this.selectedAddressSearchItem || this.selectedAddressSearchItem.text !== this.addressSearchInput);
    },
    defaultItemText(): string {
      if (this.street) {
        if (this.houseNumber) {
          return `${this.street} ${this.houseNumber}, ${this.postal} ${this.city}`.trim();
        }
        return `${this.street}, ${this.postal} ${this.city}`.trim();
      }
      return `${this.postal} ${this.city}`.trim();
    },
  },
  methods: {
    emitUpdate() {
      const place: PlaceTemplateDto = {
        street: this.street,
        houseNumber: this.houseNumber,
        postal: this.postal,
        city: this.city,
        state: this.state as StateCodes,
        country: this.country as CountryCodes,
      };
      if (typeof this.lat !== 'undefined' && typeof this.lng !== 'undefined') {
        place.location = {
          type: 'Point',
          coordinates: [this.lng, this.lat],
        };
      }
      this.$emit(`input`, place);
    },
    handleAddressSearchInput() {
      if (this.selectedAddressSearchItem) {
        const place = this.selectedAddressSearchItem.value;
        this.street = place.street;
        this.houseNumber = place.houseNumber as string;
        this.postal = place.postal;
        this.city = place.city;
        this.state = place.state as StateCodes;
        this.country = place.country;
        this.lng = place.location.coordinates[0];
        this.lat = place.location.coordinates[1];
      } else {
        this.street = '';
        this.houseNumber = '';
        this.postal = '';
        this.city = '';
        this.state = null;
        this.country = null;
        this.lat = undefined;
        this.lng = undefined;
      }
      this.emitUpdate();
    },
    handleManualInput() {
      const value = {
        street: this.street,
        houseNumber: this.houseNumber,
        postal: this.postal,
        city: this.city,
        state: this.state,
        country: this.country,
        location: undefined,
      };
      const item: AddressSearchItem = {text: this.defaultItemText, value: value as any, source: 'unknown'};
      this.addressSearchItems = [item];
      this.selectedAddressSearchItem = item;
      this.lat = undefined;
      this.lng = undefined;
      this.emitUpdate();
    },
    loadDefault() {
      if (this.defaultObject) {
        this.street = this.defaultObject.street as unknown as string;
        this.houseNumber = this.defaultObject.houseNumber as unknown as string;
        this.postal = this.defaultObject.postal as unknown as string;
        this.city = this.defaultObject.city as unknown as string;
        this.state = this.defaultObject.state as unknown as StateCodes;
        this.country = this.defaultObject.country as unknown as CountryCodes;
        const item: AddressSearchItem = { text: this.defaultItemText, value: this.defaultObject as any, source: 'unknown' };
        this.addressSearchItems = [item];
        this.selectedAddressSearchItem = item;
        this.lat = this.defaultObject.location?.coordinates[1];
        this.lng = this.defaultObject.location?.coordinates[0];
        this.emitUpdate();
      }
    },
    async handleAddressSearch() {
      if (!this.hasValidSearchInput) {
        return;
      }
      this.addressSearchLoading = true;
      if (this.throttled) {
        this.throttleCounter++;
        await sleep(this.throttleDelay);
        this.throttleCounter--;
        if (this.throttleCounter > 0) {
          return;
        }
      }
      try {
        if (this.hasValidSearchInput) {
          const resp = await GeocoderAPI.findAddress({
            $search: this.addressSearchInput,
            useGoogle: this.useGoogle,
            useOsm: this.useOsm,
            requireStreet: this.requireStreet,
          });
          this.addressSearchItems = resp.items;
        }
      } finally {
        this.addressSearchLoading = false;
      }
    },
  },
  watch: {
    useManualInput(v: boolean) {
      if (!v) {
        this.street = '';
        this.houseNumber = '';
        this.postal = '';
        this.city = '';
        this.state = null;
        this.country = null;
        this.lat = undefined;
        this.lng = undefined;
        this.selectedAddressSearchItem = null;
        this.addressSearchItems = [];
        this.emitUpdate();
      }
    },
    defaultObject: {
      deep: true,
      immediate: true,
      handler() {
        this.loadDefault();
      },
    },
  },
});
</script>

<style scoped>
.create-address__manual {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 14px;
}

.create-address__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.create-address__grid--wide-narrow {
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
}

.create-address__grid--narrow-wide {
  grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
}

.create-address__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

@media (max-width: 599px) {
  .create-address__grid, .create-address__grid--wide-narrow, .create-address__grid--narrow-wide {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
